import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { PermissionGate } from '../../components/PermissionGate'
import { MarkdownRenderer } from '../../components/ui/MarkdownRenderer'
import { brainstormingApi } from '../../api/brainstorming.api'
import { BRAINSTORM_CATEGORY_LABELS, BRAINSTORM_CATEGORY_BADGE } from './brainstormLabels'
import { BrainstormEntryFormModal } from './BrainstormEntryFormModal'
import { ProgressBar } from '../tasks/shared/ProgressBar'
import { useAuth } from '../../auth/AuthContext'
import type { BrainstormCategory, BrainstormEntry } from '../../api/types'
import projectStyles from '../projects/ProjectDetail.module.css'

const CATEGORY_OPTIONS = Object.keys(BRAINSTORM_CATEGORY_LABELS) as BrainstormCategory[]

export function BrainstormingTab({ projectId }: { projectId: string }) {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('brainstorming.manage')
  const [filter, setFilter] = useState<BrainstormCategory | ''>('')
  const [formEntry, setFormEntry] = useState<BrainstormEntry | null | undefined>(undefined)

  const entriesQuery = useQuery({
    queryKey: ['brainstorming', projectId, filter],
    queryFn: async () => (await brainstormingApi.list(projectId, filter || undefined)).data,
  })
  const entries = entriesQuery.data ?? []

  return (
    <div>
      <div className={projectStyles.filterBar}>
        <button className={[projectStyles.tab, filter === '' ? projectStyles.tabActive : ''].join(' ')} onClick={() => setFilter('')}>
          Alle
        </button>
        {CATEGORY_OPTIONS.map((c) => (
          <button key={c} className={[projectStyles.tab, filter === c ? projectStyles.tabActive : ''].join(' ')} onClick={() => setFilter(c)}>
            {BRAINSTORM_CATEGORY_LABELS[c]}
          </button>
        ))}
        <PermissionGate anyOf={['brainstorming.manage']}>
          <Button size="sm" variant="primary" onClick={() => setFormEntry(null)} style={{ marginLeft: 'auto' }}>
            + Neuer Eintrag
          </Button>
        </PermissionGate>
      </div>

      {entries.length === 0 ? (
        <EmptyState
          icon="💡"
          title="Noch nichts gesammelt"
          description="Konzeptideen, Grundgerüstscripts, Gedankengänge oder Anforderungen — alles, was noch keinen festen Platz hat."
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-3)' }}>
          {entries.map((entry) => (
            <Card key={entry.id} style={{ cursor: canManage ? 'pointer' : 'default' }}>
              <div onClick={() => (canManage ? setFormEntry(entry) : undefined)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                  <Badge variant={BRAINSTORM_CATEGORY_BADGE[entry.category]}>{BRAINSTORM_CATEGORY_LABELS[entry.category]}</Badge>
                </div>
                <h3 style={{ margin: '0 0 var(--space-2)' }}>{entry.title}</h3>
                <ProgressBar value={entry.progress} size="sm" />
                <div style={{ marginTop: 'var(--space-2)', maxHeight: 220, overflow: 'auto' }}>
                  <MarkdownRenderer content={entry.body} />
                </div>
                <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {entry.creator.displayName} · {new Date(entry.createdAt).toLocaleDateString('de-DE')}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {formEntry !== undefined ? <BrainstormEntryFormModal projectId={projectId} entry={formEntry} onClose={() => setFormEntry(undefined)} /> : null}
    </div>
  )
}
